import type {
  CellStateDto,
  RateSeniorityLevelDto,
  SubmissionStateDto,
} from '@/api/rate-collection-client';
import type { BadgeVariant } from '@/lib/supplier-labels';

// =============================================================================
// rate-collection-labels.ts — Mapping enums ASCII (API) → libellés FR + variant.
// =============================================================================
// L'API porte des enums ASCII (parité Prisma) ; l'UI les rend en libellés FR
// accentués (i18n front). WCAG 2.1 AA (ST-NFR-03) : la COULEUR n'est JAMAIS le
// seul porteur d'information — chaque état rend un LIBELLÉ TEXTE ; le variant de
// badge n'est qu'un renfort visuel. Table de traduction pure (aucune règle métier).

interface LabelWithVariant {
  readonly label: string;
  readonly variant: BadgeVariant;
}

/** Niveaux de séniorité (ordre d'affichage colonne, croissant). */
export const SENIORITY_LEVELS_ORDERED: readonly RateSeniorityLevelDto[] = [
  'Debutant',
  'Maitrisant',
  'Expert',
  'Referent',
];

/** Niveau de séniorité → libellé FR (en-têtes de colonnes de la grille). */
const SENIORITY_LABELS: Record<RateSeniorityLevelDto, string> = {
  Debutant: 'Débutant',
  Maitrisant: 'Maîtrisant',
  Expert: 'Expert',
  Referent: 'Référent',
};

/** Libellé FR d'un niveau de séniorité. */
export function seniorityLabel(level: RateSeniorityLevelDto): string {
  return SENIORITY_LABELS[level];
}

/** État d'une submission → libellé FR + variant de badge. */
const SUBMISSION_STATE_LABELS: Record<SubmissionStateDto, LabelWithVariant> = {
  Vide: { label: 'Vide', variant: 'outline' },
  Brouillon: { label: 'Brouillon', variant: 'secondary' },
  Soumis: { label: 'Soumise', variant: 'default' },
  Verrouille: { label: 'Verrouillée', variant: 'accent' },
  ReModifiable: { label: 'Ré-modifiable', variant: 'secondary' },
};

/** Libellé FR + variant d'un état de submission. */
export function submissionStateLabel(state: SubmissionStateDto): LabelWithVariant {
  return SUBMISSION_STATE_LABELS[state];
}

/** État d'une cellule → libellé FR (renfort lecteur d'écran, jamais la couleur seule). */
const CELL_STATE_LABELS: Record<CellStateDto, string> = {
  Vide: 'Non saisie',
  Saisie: 'Saisie',
  Soumise: 'Soumise',
};

/** Libellé FR d'un état de cellule. */
export function cellStateLabel(state: CellStateDto): string {
  return CELL_STATE_LABELS[state];
}

/** Libellé d'affichage d'un profil : son libellé si présent, sinon son code (sans troncature). */
export function profileDisplayLabel(
  profileCode: string,
  profileLabel: string | null,
): string {
  return profileLabel ?? profileCode;
}
